import React, {useEffect, useRef, useState} from 'react';
import {Link, useLocation, useNavigate, useParams} from 'react-router-dom';
import {QRCodeSVG} from 'qrcode.react';
import Layout from '../components/Layout';
import {api, API_BASE, getCookie} from '../../api';


export default function ItemDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [box, setBox] = useState(null);
  const [boxItems, setBoxItems] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [photoUrls, setPhotoUrls] = useState({});
  const [loading, setLoading] = useState(true);
  const [saved, setSaved] = useState(location.state?.saved || false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItemDesc, setNewItemDesc] = useState('');
  const [showQr, setShowQr] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletePhotoTarget, setDeletePhotoTarget] = useState(null);
  const [itemPhotoUrls, setItemPhotoUrls] = useState({});
  const [deleteItemPhotoTarget, setDeleteItemPhotoTarget] = useState(null);
  const [lightboxPhoto, setLightboxPhoto] = useState(null);
  const [lightboxItemPhoto, setLightboxItemPhoto] = useState(null);
  const qrRef = useRef();
  const photoInputRef = useRef();
  const itemPhotoInputRef = useRef();
  const [itemPhotoTarget, setItemPhotoTarget] = useState(null);

  useEffect(() => {
    if (saved) {
      const timer = setTimeout(() => setSaved(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [saved]);

  const fetchData = () => {
    const hasTenant = document.cookie.split(';').some(c => c.trim().startsWith('tenantId='));
    if (!hasTenant) {
      navigate(`/tenants?redirect=${encodeURIComponent(location.pathname)}`);
      return;
    }
    Promise.all([
      api(`/boxes/${id}`).then(res => res.ok ? res.json() : null),
      api(`/boxes/${id}/items`).then(res => res.ok ? res.json() : []),
      api(`/boxes/${id}/photos`).then(res => res.ok ? res.json() : []),
    ]).then(([boxData, itemsData, photosData]) => {
      setBox(boxData);
      const items = Array.isArray(itemsData) ? itemsData : [];
      setBoxItems(items);
      const photoList = Array.isArray(photosData) ? photosData : [];
      setPhotos(photoList);
      setLoading(false);
      photoList.forEach(p => loadPhotoUrl(p.id));
      items.filter(i => i.photoFileName).forEach(i => loadItemPhotoUrl(i.id));
    }).catch(() => setLoading(false));
  };

  const loadPhotoUrl = (photoId) => {
    api(`/boxes/${id}/photos/${photoId}`).then(res => res.ok ? res.json() : null).then(data => {
      if (data?.url) setPhotoUrls(prev => ({ ...prev, [photoId]: data.url }));
    });
  };

  const loadItemPhotoUrl = (itemId) => {
    api(`/boxes/${id}/items/${itemId}/photo`).then(res => res.ok ? res.json() : null).then(data => {
      if (data?.url) setItemPhotoUrls(prev => ({ ...prev, [itemId]: data.url }));
    });
  };

  useEffect(() => { fetchData(); }, [id]);

  const confirmDelete = () => {
    if (deleteTarget === 'box') {
      api(`/boxes/${id}`, { method: 'DELETE' })
        .then(() => navigate('/storage/boxes'));
    } else {
      api(`/boxes/${id}/items/${deleteTarget}`, { method: 'DELETE' })
        .then(() => { setDeleteTarget(null); fetchData(); });
    }
  };

  const handleAddItem = (e) => {
    e.preventDefault();
    api(`/boxes/${id}/items`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ description: newItemDesc }),
    }).then(() => {
      setShowAddItem(false);
      setNewItemDesc('');
      fetchData();
    });
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    const jwtToken = getCookie('jwtToken');
    const tenantId = getCookie('tenantId');
    const headers = {};
    if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
    if (tenantId) headers['X-Tenant-Id'] = tenantId;
    try {
      const res = await fetch(`${API_BASE}/boxes/${id}/photos`, { method: 'POST', headers, body: formData });
      if (res.ok) fetchData();
    } finally {
      setUploading(false);
      photoInputRef.current.value = '';
    }
  };

  const confirmDeletePhoto = () => {
    api(`/boxes/${id}/photos/${deletePhotoTarget}`, { method: 'DELETE' }).then(() => {
      setDeletePhotoTarget(null);
      setPhotoUrls(prev => { const next = { ...prev }; delete next[deletePhotoTarget]; return next; });
      fetchData();
    });
  };

  const handleItemPhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file || !itemPhotoTarget) return;
    const formData = new FormData();
    formData.append('file', file);
    const jwtToken = getCookie('jwtToken');
    const tenantId = getCookie('tenantId');
    const headers = {};
    if (jwtToken) headers['Authorization'] = `Bearer ${jwtToken}`;
    if (tenantId) headers['X-Tenant-Id'] = tenantId;
    await fetch(`${API_BASE}/boxes/${id}/items/${itemPhotoTarget}/photo`, { method: 'POST', headers, body: formData });
    setItemPhotoTarget(null);
    itemPhotoInputRef.current.value = '';
    fetchData();
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!box) return <div className="p-6">Box not found.</div>;

  return (
    <Layout>
      {saved && (
        <div className="mb-4 px-4 py-3 bg-green-100 text-green-800 rounded-lg">Box saved successfully.</div>
      )}
      <Link to="/storage/boxes" className="text-green-600 hover:text-green-800 mb-4 inline-block">&larr; Back to Boxes</Link>
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">{box.name || 'Box Detail'}</h1>
          <div className="flex gap-3">
            <button onClick={() => setShowQr(true)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Print QR Code</button>
            <Link to={`/storage/boxes/${id}/edit`} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Edit</Link>
            <button onClick={() => setDeleteTarget('box')} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Delete</button>
          </div>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-500">Name</label>
          <p className="mt-1 text-gray-900">{box.name || '—'}</p>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-500">Location</label>
          <p className="mt-1 text-gray-900">{box.location || '—'}</p>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-500">Shelf</label>
          <p className="mt-1 text-gray-900">{box.shelf || '—'}</p>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-500">Description</label>
          <p className="mt-1 text-gray-900">{box.description || '—'}</p>
        </div>
        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-500">Created</label>
          <p className="mt-1 text-gray-900">{new Date(box.createdAt).toLocaleString()}</p>
        </div>
        <div className="mb-6">
          <label className="block text-sm font-medium text-gray-500">Updated</label>
          <p className="mt-1 text-gray-900">{new Date(box.updatedAt).toLocaleString()}</p>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow mb-6">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Photos ({photos.length})</h2>
            <div>
              <input ref={photoInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handlePhotoUpload} />
              <button onClick={() => photoInputRef.current.click()} disabled={uploading}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                {uploading ? 'Uploading...' : 'Add Photo'}
              </button>
            </div>
          </div>
        </div>
        {photos.length > 0 ? (
          <div className="p-6 grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4">
            {photos.map(photo => (
              <div key={photo.id} className="relative group">
                {photoUrls[photo.id] ? (
                  <img src={photoUrls[photo.id]} alt={photo.fileName} className="w-full h-40 object-cover rounded-lg cursor-pointer" onClick={() => setLightboxPhoto(photo)} />
                ) : (
                  <div className="w-full h-40 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">Loading...</div>
                )}
                <button onClick={() => setDeletePhotoTarget(photo.id)}
                  className="absolute top-2 right-2 bg-white bg-opacity-80 rounded-full p-1 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-100">
                  <svg className="w-4 h-4 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-gray-500">No photos yet. Take a photo to get started.</div>
        )}
      </div>

      <div className="bg-white rounded-lg shadow">
        <div className="p-6 border-b">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-bold text-gray-900">Items ({boxItems.length})</h2>
            <button onClick={() => setShowAddItem(true)} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">
              Add Item
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Photo</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Description</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {boxItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    {itemPhotoUrls[item.id] ? (
                      <img src={itemPhotoUrls[item.id]} alt={item.photoFileName} className="w-12 h-12 object-cover rounded cursor-pointer" onClick={() => setLightboxItemPhoto(item)} />
                    ) : (
                      <div className="w-12 h-12 bg-gray-100 rounded flex items-center justify-center text-gray-300">
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
                      </div>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-900">{item.description || '—'}</td>
                  <td className="px-6 py-4 whitespace-nowrap text-gray-600">
                    {new Date(item.createdAt).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button onClick={() => { setItemPhotoTarget(item.id); itemPhotoInputRef.current.click(); }} className="text-green-600 hover:text-green-800 font-medium mr-3">{item.photoFileName ? 'Replace' : 'Add'} Photo</button>
                    {item.photoFileName && <button onClick={() => setDeleteItemPhotoTarget(item.id)} className="text-gray-500 hover:text-red-600 font-medium mr-3">Remove Photo</button>}
                    <button onClick={() => setDeleteTarget(item.id)} className="text-gray-500 hover:text-red-600 font-medium">Delete</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {boxItems.length === 0 && (
            <div className="text-center py-12 text-gray-500">No items in this box yet.</div>
          )}
        </div>
      </div>

      <input ref={itemPhotoInputRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={handleItemPhotoUpload} />

      {deleteTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-96">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Confirm Delete</h3>
            <p className="text-gray-600 mb-6">
              {deleteTarget === 'box'
                ? 'Are you sure you want to delete this box? This will also delete all items inside it.'
                : 'Are you sure you want to delete this item?'}
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteTarget(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={confirmDelete} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      {deletePhotoTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-96">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Delete Photo</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to delete this photo?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeletePhotoTarget(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={confirmDeletePhoto} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Delete</button>
            </div>
          </div>
        </div>
      )}

      {deleteItemPhotoTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-96">
            <h3 className="text-lg font-bold text-gray-900 mb-2">Remove Item Photo</h3>
            <p className="text-gray-600 mb-6">Are you sure you want to remove this item's photo?</p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setDeleteItemPhotoTarget(null)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
              <button onClick={() => {
                api(`/boxes/${id}/items/${deleteItemPhotoTarget}/photo`, { method: 'DELETE' }).then(() => {
                  setItemPhotoUrls(prev => { const next = { ...prev }; delete next[deleteItemPhotoTarget]; return next; });
                  setDeleteItemPhotoTarget(null);
                  fetchData();
                });
              }} className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Remove</button>
            </div>
          </div>
        </div>
      )}

      {showQr && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-96 text-center" ref={qrRef}>
            <h3 className="text-2xl font-bold text-gray-900 mb-4">{box.name}</h3>
            <div className="relative mx-auto mb-4" style={{width: 256, height: 256}}>
              <QRCodeSVG
                value={`${window.location.origin}/storage/boxes/${id}`}
                size={256}
                level="H"
              />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="bg-white px-2 py-1 text-xs font-bold text-gray-900">Matter King</span>
              </div>
            </div>

            <div className="flex justify-center gap-3 print-hide">
              <button onClick={() => setShowQr(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Close</button>
              <button
                onClick={() => {
                  const svgHtml = qrRef.current.querySelector('.relative').querySelector('svg').outerHTML;
                  const win = window.open('', '_blank');
                  win.document.write(`<html><head><meta name="viewport" content="width=device-width,initial-scale=1"><title>QR - ${box.name}</title><style>*{margin:0;padding:0;box-sizing:border-box}@page{margin:10mm}body{font-family:sans-serif}.c{text-align:center;padding:10px}h3{font-size:20px;font-weight:bold;margin-bottom:12px}.q{position:relative;width:50vw;max-width:200px;margin:0 auto}.q svg{width:100%;height:auto}.l{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);background:#fff;padding:2px 6px;font-size:10px;font-weight:bold}</style></head><body><div class="c"><h3>${box.name}</h3><div class="q">${svgHtml}<span class="l">Matter King</span></div></div></body></html>`);
                  win.document.close();
                  win.print();
                }}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
              >Print</button>
            </div>
          </div>
        </div>
      )}

      {lightboxItemPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50" onClick={() => setLightboxItemPhoto(null)}>
          <div className="relative max-w-4xl max-h-[90vh] p-2" onClick={e => e.stopPropagation()}>
            <button onClick={() => setLightboxItemPhoto(null)} className="absolute top-4 right-4 bg-white bg-opacity-80 rounded-full p-2 hover:bg-opacity-100 z-10">
              <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img src={itemPhotoUrls[lightboxItemPhoto.id]} alt={lightboxItemPhoto.photoFileName} className="max-w-full max-h-[85vh] object-contain rounded-lg" />
          </div>
        </div>
      )}

      {lightboxPhoto && (
        <div className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50" onClick={() => setLightboxPhoto(null)}>
          <div className="relative max-w-4xl max-h-[90vh] p-2" onClick={e => e.stopPropagation()}>
            <button onClick={() => setLightboxPhoto(null)} className="absolute top-4 right-4 bg-white bg-opacity-80 rounded-full p-2 hover:bg-opacity-100 z-10">
              <svg className="w-5 h-5 text-gray-800" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
            <img src={photoUrls[lightboxPhoto.id]} alt={lightboxPhoto.fileName} className="max-w-full max-h-[85vh] object-contain rounded-lg" />
          </div>
        </div>
      )}

      {showAddItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 w-96">
            <h3 className="text-lg font-bold text-gray-900 mb-4">Add Item</h3>
            <form onSubmit={handleAddItem}>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea
                  value={newItemDesc}
                  onChange={(e) => setNewItemDesc(e.target.value)}
                  rows={3}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-green-600"
                />
              </div>
              <div className="flex justify-end gap-3">
                <button type="button" onClick={() => setShowAddItem(false)} className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">Cancel</button>
                <button type="submit" className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700">Add</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </Layout>
  );
}
